import { collection, query, where, getDocs, getDocsFromCache } from 'firebase/firestore';
import { pendingRunSortMillis } from './pendingRunsQueue';

/**
 * Document payload for UI: spread fields first, then real Firestore doc id.
 * If `{ id: doc.id, ...data }` is used, a stored `id` field overwrites the doc id and
 * delete / open-run target the wrong path → permission errors.
 */
export function firestoreDocWithId(docSnap) {
  return { ...docSnap.data(), id: docSnap.id };
}

/**
 * Sort by activity time (same key as Home "Recent rides" and My Rides date-desc).
 */
export function sortRunsByActivityDesc(runs) {
  return [...(runs || [])].sort(
    (a, b) =>
      pendingRunSortMillis(b.timestamp || b.date || b.createdAt) -
      pendingRunSortMillis(a.timestamp || a.date || a.createdAt),
  );
}

async function mergeRunSnapsIntoSortedList(snapUser, snapOwner) {
  const byId = new Map();
  snapUser.docs.forEach((d) => {
    byId.set(d.id, firestoreDocWithId(d));
  });
  snapOwner.docs.forEach((d) => {
    if (!byId.has(d.id)) byId.set(d.id, firestoreDocWithId(d));
  });
  return sortRunsByActivityDesc(Array.from(byId.values()));
}

/** @param {(q: any) => Promise<{ docs: { id: string, data: () => object }[] }>} readDocs */
async function fetchOwnRunsWithReader(readDocs, db, userId) {
  if (!userId) return [];
  const runsRef = collection(db, 'runs');
  const [snapUser, snapOwner] = await Promise.all([
    readDocs(query(runsRef, where('userId', '==', userId))),
    readDocs(query(runsRef, where('ownerId', '==', userId))).catch(() => ({ docs: [] })),
  ]);
  return mergeRunSnapsIntoSortedList(snapUser, snapOwner);
}

/**
 * All run documents owned by this user (userId and/or legacy ownerId, e.g. Dashboard).
 *
 * Do not use orderBy('createdAt') here: Firestore omits documents that lack that field
 * from composite index queries, so the newest run can disappear from Home / My Rides
 * while still existing in the database.
 *
 * @param {{ timeoutMs?: number }} [options] — When `timeoutMs` is set, the server read is raced
 * against that deadline; if it does not resolve (e.g. captive logger Wi‑Fi with no Firestore route),
 * a cache-only read is attempted, then [] so callers do not hang forever.
 */
export async function fetchOwnRunsForUserFirestore(db, userId, options = {}) {
  const timeoutMs = typeof options.timeoutMs === 'number' && Number.isFinite(options.timeoutMs) ? options.timeoutMs : null;
  const runServer = () => fetchOwnRunsWithReader((q) => getDocs(q), db, userId);
  if (!timeoutMs || timeoutMs <= 0) return runServer();
  try {
    return await Promise.race([
      runServer(),
      new Promise((_, reject) => {
        setTimeout(
          () =>
            reject(
              Object.assign(new Error('fetchOwnRunsForUserFirestore: server read timed out'), {
                code: 'runs-fetch-timeout',
              })
            ),
          timeoutMs
        );
      }),
    ]);
  } catch (e) {
    if (e?.code !== 'runs-fetch-timeout') throw e;
    try {
      return await fetchOwnRunsWithReader((q) => getDocsFromCache(q), db, userId);
    } catch (_cacheErr) {
      return [];
    }
  }
}
